

import { useState } from "react";
import Modal from "./Modal";
import { useAuth } from "@/hooks/useAuth";
import { profileApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AccountSettingsModal({ isOpen, onClose }: AccountSettingsModalProps) {
  const { user, setUser } = useAuth();
  const { showToast } = useToast();
  
  // Profile form state
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleUpdateProfile = async () => {
    setErrors({});
    setLoading(true);
    
    try {
      const response = await profileApi.update({ name, email });
      const userData = response.data || response;
      
      // Update user in auth context
      if (setUser && user) {
        setUser({
          ...user,
          name: userData.name || name,
          email: userData.email || email,
        });
      }
      
      showToast("Profile updated successfully", "success");
    } catch (error: any) {
      if (error.message?.includes("Validation Error")) {
        setErrors({ email: "Email may already be taken or invalid" });
      } else {
        showToast(error.message || "Failed to update profile", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setErrors({});
    
    // Client-side validation
    const newErrors: Record<string, string> = {};
    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      await profileApi.changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      
      showToast("Password changed successfully", "success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      if (error.message?.includes("current_password")) {
        setErrors({ currentPassword: "Current password is incorrect" });
      } else {
        showToast(error.message || "Failed to change password", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Account Settings" size="md">
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200 pb-2">
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === "profile"
                ? "bg-amber-50 text-amber-700 border border-amber-200"
                : "text-stone-500 hover:bg-stone-100"
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === "password"
                ? "bg-amber-50 text-amber-700 border border-amber-200"
                : "text-stone-500 hover:bg-stone-100"
            }`}
          >
            Change Password
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                placeholder="Your name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 ${
                  errors.email ? "border-red-300 bg-red-50" : "border-stone-200"
                }`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Role
              </label>
              <input
                type="text"
                value={user?.role || ""}
                disabled
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-100 text-stone-500 capitalize"
              />
            </div>

            <button
              onClick={handleUpdateProfile}
              disabled={loading}
              className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === "password" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 ${
                  errors.currentPassword ? "border-red-300 bg-red-50" : "border-stone-200"
                }`}
                placeholder="Enter current password"
              />
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 ${
                  errors.newPassword ? "border-red-300 bg-red-50" : "border-stone-200"
                }`}
                placeholder="Enter new password"
              />
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 ${
                  errors.confirmPassword ? "border-red-300 bg-red-50" : "border-stone-200"
                }`}
                placeholder="Confirm new password"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              onClick={handleChangePassword}
              disabled={loading}
              className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Changing...
                </>
              ) : (
                "Change Password"
              )}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
